/**
 * Paddle Billing client — loads Paddle.js, initialises the environment
 * and opens the Overlay Checkout with VAULT metadata. NEVER touches
 * raw card data (Paddle's overlay hosts the card form itself).
 */
import { api } from "@/lib/api";

const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

let _loading = null;

function loadScript() {
    if (window.Paddle) return Promise.resolve();
    if (_loading) return _loading;
    _loading = new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = PADDLE_JS;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load Paddle.js"));
        document.head.appendChild(s);
    });
    return _loading;
}

let _configPromise = null;
export function getBillingConfig() {
    if (!_configPromise) {
        _configPromise = api.get("/billing/config").then((r) => r.data).catch(() => ({
            checkout_configured: false, plans: [], environment: "sandbox", mock_mode: true,
        }));
    }
    return _configPromise;
}

export async function initPaddleIfConfigured() {
    const cfg = await getBillingConfig();
    if (!cfg.checkout_configured || !cfg.client_token) return { ready: false, cfg };
    await loadScript();
    const env = cfg.environment === "production" ? "production" : "sandbox";
    window.Paddle.Environment.set(env);
    window.Paddle.Initialize({ token: cfg.client_token });
    return { ready: true, cfg };
}

/**
 * Open the Paddle Overlay Checkout. Custom metadata is forwarded to
 * every subscription/transaction event so the webhook can resolve the
 * VAULT user & plan.
 */
export async function openCheckout({ priceId, userId, planSlug, email, onClose, onEvent }) {
    const { ready } = await initPaddleIfConfigured();
    if (!ready) throw new Error("Paddle checkout not configured");
    window.Paddle.Checkout.open({
        settings: {
            displayMode: "overlay",
            theme: "light",
            locale: (navigator.language || "en").split("-")[0],
            successUrl: `${window.location.origin}/checkout/success`,
        },
        items: [{ priceId, quantity: 1 }],
        customer: email ? { email } : undefined,
        customData: { user_id: userId, plan_slug: planSlug },
        eventCallback: (event) => {
            if (event?.name === "checkout.closed" && onClose) onClose(event);
            if (onEvent) onEvent(event);
        },
    });
}

// Small helpers for preserving plan/billing through auth.
export const PLAN_STORAGE_KEY = "vault.pending.plan";
export function stashPendingPlan(params) {
    try {
        sessionStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify({
            plan: params.plan || null,
            billing: params.billing || "monthly",
            currency: params.currency || null,
            country: params.country || null,
            ref: params.ref || null,
            promo: params.promo || null,
            stashed_at: Date.now(),
        }));
    } catch { /* private mode */ }
}
export function readPendingPlan() {
    try {
        const raw = sessionStorage.getItem(PLAN_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
export function clearPendingPlan() {
    try { sessionStorage.removeItem(PLAN_STORAGE_KEY); } catch { /* private mode */ }
}
